import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faUser,
  faLock,
  faXmark,
  faEye,
  faEyeSlash,
  faSun,
  faMoon,
  faDesktop,
  faCircleInfo,
} from '@fortawesome/pro-solid-svg-icons';
import { Auth } from '../../../core/auth/auth';
import { ContractStatusService } from '../../../core/services/contract-status.service';
import { PopupInfoService } from '../../../shared/services/popup-info.service';
import { User } from '../../../core/models/user.model';
import { ThemeService, Theme } from '../../../core/services/theme.service';

@Component({
  selector: 'app-profile-page',
  imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule, RouterLink],
  templateUrl: './profile-page.html',
  styleUrls: ['./profile-page.scss'],
})
export class ProfilePage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(Auth);
  private readonly router = inject(Router);
  private readonly popupInfoService = inject(PopupInfoService);
  public readonly themeService = inject(ThemeService);
  readonly contractStatusService = inject(ContractStatusService);

  // Icons
  faUser = faUser;
  faLock = faLock;
  faXmark = faXmark;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faSun = faSun;
  faMoon = faMoon;
  faDesktop = faDesktop;
  faCircleInfo = faCircleInfo;

  profileForm: FormGroup;
  passwordForm: FormGroup;
  isLoading = false;
  isProfileLoading = true;
  error: string | null = null;
  showPasswordModal = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  constructor() {
    this.profileForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
    });

    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadUserData();
  }

  loadUserData(): void {
    if (this.auth.infoProfile) {
      this.setDataFromUser(this.auth.infoProfile);
      this.isProfileLoading = false;
    } else {
      this.auth.getProfile().subscribe({
        next: (user) => {
          if (user) {
            this.setDataFromUser(user);
          }
          this.isProfileLoading = false;
        },
        error: () => {
          this.isProfileLoading = false;
        },
      });
    }
  }

  setDataFromUser(user: User) {
    this.profileForm.patchValue({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
    });
  }

  onSubmit(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.error = null;

    const userData = this.profileForm.value;

    this.auth.updateMe(userData).subscribe({
      next: (res) => {
        this.popupInfoService.showSuccess('Perfil actualizado correctamente');
        this.isLoading = false;
        this.auth.updateUserData(res.data);
        this.profileForm.markAsPristine();
      },
      error: () => {
        const errorMessage =
          'Error al actualizar el perfil, por favor inténtalo de nuevo más tarde.';
        this.error = errorMessage;
        this.popupInfoService.showError(errorMessage);
        this.isLoading = false;
      },
    });
  }

  onCancel(): void {
    this.profileForm.markAsPristine();
    this.loadUserData();
  }

  openPasswordModal(): void {
    this.showPasswordModal = true;
  }

  closePasswordModal(): void {
    this.showPasswordModal = false;
    this.passwordForm.reset();
    this.passwordForm.markAsPristine();
    this.showCurrentPassword = false;
    this.showNewPassword = false;
    this.showConfirmPassword = false;
  }

  onChangePassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const { newPassword, confirmPassword, currentPassword } = this.passwordForm.value;

    if (newPassword !== confirmPassword) {
      this.popupInfoService.showError('Las contraseñas no coinciden');
      return;
    }

    this.isLoading = true;

    this.auth.changePassword(currentPassword, newPassword, confirmPassword).subscribe({
      next: () => {
        this.popupInfoService.showSuccess('Contraseña actualizada correctamente');
        this.passwordForm.reset();
        this.passwordForm.markAsPristine();
        this.isLoading = false;
        this.closePasswordModal();
      },
      error: () => {
        const msg = 'Error al cambiar la contraseña, por favor inténtalo de nuevo más tarde.';
        this.popupInfoService.showError(msg);
        this.isLoading = false;
      },
    });
  }

  onCancelPassword(): void {
    this.passwordForm.reset();
    this.passwordForm.markAsPristine();
  }

  get f() {
    return this.profileForm.controls;
  }

  get fp() {
    return this.passwordForm.controls;
  }

  get currentUser() {
    return this.auth.currentUser;
  }

  toggleCurrentPassword(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPassword(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  setTheme(theme: Theme): void {
    this.themeService.setTheme(theme);
  }
}
